import React from 'react';
import { css } from '@emotion/react';
import { color } from '../../../../styles';
import { CARE_RECORDS } from '../../../../constants';
import { IGetDfreeBDeviceAnalyticsResult } from '../../../../types/api/traversal/getDfreeBDeviceAnalytics';
import { DEFECATION_ANALYTICS } from '../../../../constants/defecationAnalytics';
import { svgIcons } from '.';
import { useTranslation } from 'react-i18next';
import { media } from '@/styles/media';

interface Props {
  data: IGetDfreeBDeviceAnalyticsResult;
}

const TextSize = (size: string) => css`
  font-size: ${size};
`;

const DefecationBalloonContainer = css`
  flex: 1;
  min-width: 170px;

  ${media.minDesktop} {
    height: 0;
  }
`;

const TableContainer = css`
  flex: 1;
`;

const GridTableStyle = css`
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: repeat(7, auto);
  gap: 1px;
  border: 1px solid ${color.border_gray};
  background-color: ${color.border_gray};
`;

const GridHeaderCellStyle = css`
  background-color: #f0f0f0;
  padding: 10px;
  text-align: left;
  white-space: nowrap;
`;

const GridDataCellStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  padding: 0 12px;
  vertical-align: middle;
  text-align: left;
  white-space: nowrap;
`;

const IconStyle = css`
  display: inline-block;
  text-align: center;
  margin-right: 8px;
  width: 32px;
`;

const CounterContainer = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-end;
  font-weight: bold;
  color: ${color.dark_gray};
  gap: 6px;
  margin-bottom: 8px;
`;

const CounterTitle = css`
  max-width: 100px;
`;

const CounterResult = css`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 2px;
`;

// DefecationBalloonComponent
const BalloonStyle = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  top: 16px;
  left: 50%;
  transform: translate(-50%, 0);
  padding: 8px 32px;
  color: #fff;
  font-weight: bold;
  background: #ff6381;
  border-radius: 10px;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -15px;
    border: 15px solid transparent;
    border-top: 15px solid #ff6381;
  }
`;

const BalloonResult = css`
  width: 100%;
  padding: 24px 8px 8px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #555;
  font-weight: bold;
  background: #ffd3db;
  border-radius: 10px;
`;

const BalloonCounter = css`
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
`;

const BalloonCounterNumber = css`
  line-height: 1.4em;
  font-size: 64px;
`;

const BalloonSmallText = css`
  font-size: 14px;
  font-weight: normal;
`;

export const AnalyticsUpperRightContents = React.memo(({ data }: Props) => {
  const { t, i18n } = useTranslation();
  type FecesTypeKeys = keyof typeof data.countFecesTypes;
  const hardnessTypeLabels = i18n.language.startsWith('ja')
    ? DEFECATION_ANALYTICS.HARDNESS_TYPES_JA
    : DEFECATION_ANALYTICS.HARDNESS_TYPES_EN;

  const DefecationBalloon = React.useCallback(
    ({ count, rate }: { count: number; rate: number }) => {
      return (
        <>
          <div css={BalloonStyle}>
            <span css={TextSize('18px')}>{t('fecalContamination', '便汚染')}</span>
          </div>
          <div css={BalloonResult}>
            <div css={BalloonCounter}>
              <span css={BalloonCounterNumber}>{count}</span>
              <span css={TextSize('24px')}>{t('times', '回')}</span>
            </div>
            <div css={BalloonSmallText}>{`（${t('contaminationRate', '汚染率')}：${isNaN(rate) ? 0 : rate}%）`}</div>
          </div>
        </>
      );
    },
    [data]
  );

  return (
      <>
        <div css={DefecationBalloonContainer}>
          <DefecationBalloon count={data.countLeak} rate={Math.round((data.countLeak / data.countFeces) * 1000) / 10} />
        </div>
        <div css={TableContainer}>
          <div css={CounterContainer}>
            <span css={CounterTitle}>{t('totalBowelMovements', '合計排便')} : </span>
            <div css={CounterResult}>
            <span css={TextSize('28px')}>
              {data.countFeces}
              <span css={TextSize('18px')}>{t('times', '回')}</span>
            </span>

              <span>
              （{data.countDayOfFeces}
                {i18n.language.startsWith('ja') ? '' : ' '}
                {t('day', '日')}）
            </span>
            </div>
          </div>
          <div css={GridTableStyle}>
            {CARE_RECORDS.HARDNESS_TYPE_VALUES.map((v, index) => (
                <React.Fragment key={v.value}>
                  <div css={GridHeaderCellStyle}>
                <span css={IconStyle}>
                  <img
                      src={svgIcons[v.value]}
                      alt={`${hardnessTypeLabels[v.value as keyof typeof hardnessTypeLabels]}`}
                  />
                </span>
                    {hardnessTypeLabels[v.value as keyof typeof hardnessTypeLabels]}
                  </div>
                  <div css={GridDataCellStyle}>{`${data.countFecesTypes[v.value as FecesTypeKeys] ?? 0}${t(
                      'times',
                      '回'
                  )}`}</div>
                </React.Fragment>
            ))}
          </div>
        </div>
      </>
  );
});
